import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import RightSheet from "../../components/ui-components/RightSheet";
import RoleCard, { templateTier, groupCapabilitiesByFriendlyLabel } from "./RoleCard";
import RoleCapabilityGroups from "./RoleCapabilityGroups";
import { ACCESS_CONFIG, accessLevel, accessSummary, includesSensitiveData } from "./capabilityAccess";

// Moved here from the page's old TemplatesInfoStrip when Templates stopped
// being a tab — the statement that templates grant no access has to travel
// with the templates, not stay behind on a surface that no longer shows them.
const INTRO =
  "Maintained by InstantView and can't be edited. They aren't assigned to users and don't grant access — " +
  "using a template creates a new organisation role you can customize.";

// What each template is for, in one sentence, for the detail level's summary.
// The card's AUDIENCE_BY_CODE names the audience in two words and the seeded
// `purpose` names the activity in a fragment; neither answers "is this the
// one I want?" on its own, and the seeded `description` is written as a
// capability recital ("Create/manage budgets, forecasts, ...") rather than as
// a sentence about people. Keyed by code for the same reason the card's maps
// are: the template set is the fixed, code-seeded list in the backend's
// authz.SystemRoleTypes. A code with no entry falls back to `purpose`.
const INTENT_BY_CODE = {
  finance_admin:
    "For finance operations staff who set up how cost is measured — dimensions, allocation models, reporting settings and period locks.",
  finance_manager:
    "For finance users responsible for budgeting, invoice review, reporting and financial investigations.",
  cost_owner:
    "For budget owners who prepare and submit changes to the budgets they own, and explain the variances on them.",
  tech_cost_owner:
    "For engineering and platform owners who investigate cost drivers and propose allocation changes, without touching budgets.",
  exec_viewer:
    "For senior leadership who read financial summaries, investigations and board reports without changing anything.",
  auditor:
    "For audit and compliance reviewers who read financial data, access and audit history, and export evidence.",
  viewer: "For everyone else in the organisation who only needs to see financial summaries.",
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// What the callout calls itself. Named from the highest level the template
// actually reaches, so "financial" only appears when financial data is in
// play and a configuration-only template isn't announced as elevated.
// Returns null when nothing in the set rises above standard — that template
// gets no callout at all rather than a reassurance nobody needs.
function calloutTitle(capabilities) {
  if (includesSensitiveData(capabilities)) return "Elevated financial access";
  const levels = new Set((capabilities || []).map(accessLevel));
  levels.delete(undefined);
  if (levels.size === 0) return null;
  if (levels.size === 1 && levels.has(ACCESS_CONFIG)) return "Configuration access";
  return "Elevated access";
}

// The detail level's overview: what the role is for, then how much of the
// product it reaches, then — separately — what it puts in the holder's hands.
//
// Three facts, three treatments. An earlier version set the purpose sentence,
// the metadata, the access statement and the read-only explanation as four
// consecutive lines of near-identical 12px grey, which merged into one
// paragraph the eye skims as a block. Risk is the one an administrator must
// not skim, so it leaves the paragraph flow entirely and becomes a boxed
// callout; the read-only explanation left this block altogether and now sits
// in the footer beside the action it describes.
function TemplateSummary({ role, capByCode }) {
  const capabilities = role.capabilities || [];
  const areaCount = groupCapabilitiesByFriendlyLabel(capabilities, capByCode).length;
  const summary = accessSummary(capabilities);
  const title = calloutTitle(capabilities);
  const sensitive = includesSensitiveData(capabilities);

  return (
    <div className="mb-8">
      <p className="text-sm text-text-2 m-0">{INTENT_BY_CODE[role.code] || role.purpose}</p>

      <div className="mt-2.5 text-xs text-text-3" data-testid="template-meta">
        {plural(capabilities.length, "capability", "capabilities")} ·{" "}
        {plural(areaCount, "functional area", "functional areas")} · Read-only template
      </div>

      {summary && title && (
        <div
          data-testid="template-access-callout"
          className="mt-4 flex items-start gap-2.5 rounded-card border border-solid border-border bg-surface-2 px-4 py-3"
        >
          <FontAwesomeIcon
            icon={
              sensitive
                ? icon({ name: "triangle-exclamation", style: "solid" })
                : icon({ name: "circle-info", style: "solid" })
            }
            className={`shrink-0 ${sensitive ? "text-warning" : "text-text-3"}`}
            style={{ fontSize: 13, marginTop: 1 }}
          />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-text m-0">{title}</p>
            <p className="mt-1 text-xs text-text-2 m-0">{summary}</p>
          </div>
        </div>
      )}
    </div>
  );
}
TemplateSummary.propTypes = {
  role: PropTypes.object.isRequired,
  capByCode: PropTypes.instanceOf(Map).isRequired,
};

function SectionHeading({ children }) {
  return (
    <div className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-text-3 mb-2">{children}</div>
  );
}
SectionHeading.propTypes = { children: PropTypes.node };

function TemplateGrid({ templates, capabilityCatalog, canManage, onViewDetail }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {templates.map((role) => (
        <RoleCard
          key={role.code}
          role={role}
          capabilityCatalog={capabilityCatalog}
          canManage={canManage}
          variant="template"
          onViewPermissions={onViewDetail}
        />
      ))}
    </div>
  );
}
TemplateGrid.propTypes = {
  templates: PropTypes.array.isRequired,
  capabilityCatalog: PropTypes.array,
  canManage: PropTypes.bool,
  onViewDetail: PropTypes.func.isRequired,
};

// The template catalogue, as a step inside role creation rather than a tab of
// its own. Two levels in one sheet: the list of templates, and one template's
// full capability list. The detail level swaps in place (RightSheet's onBack
// returns to the list) instead of stacking a second sheet — closing the detail
// should put you back where you were choosing, not back on the page.
export default function TemplatePickerSheet({
  open,
  scope = "recommended",
  templates = [],
  capabilityCatalog = [],
  canManage = false,
  isLoading = false,
  onClose,
  onCreateRole,
}) {
  const [browseAll, setBrowseAll] = useState(false);
  const [detailRole, setDetailRole] = useState(null);

  // Each opening starts fresh: reopening at scope="recommended" must not
  // inherit the previous session's expanded list or a stale detail role.
  // Keyed on scope as well as open, so a caller that re-invokes the picker at
  // a different scope without closing it first still lands on the list.
  useEffect(() => {
    if (open) {
      setBrowseAll(false);
      setDetailRole(null);
    }
  }, [open, scope]);

  const { recommended, other } = useMemo(() => {
    const rec = [];
    const oth = [];
    for (const t of templates) (templateTier(t.code) === "Core" ? rec : oth).push(t);
    return { recommended: rec, other: oth };
  }, [templates]);

  const capByCode = useMemo(() => {
    const m = new Map();
    for (const c of capabilityCatalog) m.set(c.code, c);
    return m;
  }, [capabilityCatalog]);

  const showAll = scope === "all" || browseAll;

  if (detailRole) {
    return (
      <RightSheet
        open={open}
        onClose={onClose}
        onBack={() => setDetailRole(null)}
        size="wide"
        // Name and status on one line: the badge answers "what kind of object
        // am I looking at?" at the moment the name is read, which a sentence
        // further down cannot. No subtitle — "Managed template · read-only"
        // spent a second line restating what this badge and the body's meta
        // line already say.
        title={
          <span className="inline-flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span>{detailRole.name}</span>
            <span
              data-testid="template-managed-badge"
              className="inline-block rounded-full bg-surface-2 px-2 py-0.5 text-[11px] font-semibold leading-none text-text-2 align-middle"
            >
              Managed template
            </span>
          </span>
        }
        //
        // Forces a fresh scrollable body node per level (and per template, so
        // switching detail targets without a full list round-trip still
        // starts at the top) — the list branch below reuses the same
        // RightSheet instance, so without this the body div (and its
        // scrollTop) would otherwise be reused wholesale across the swap.
        bodyKey={`detail:${detailRole.code}`}
        // The one place the read-only rule is explained, now that the overview
        // no longer says it too: next to the action it describes, where it
        // answers the question the button raises. The button names the actual
        // outcome — the next screen is the role editor, not an assignment —
        // so "Use this template" gives way to the verb that happens.
        footer={
          canManage && onCreateRole ? (
            <div className="flex items-center justify-between gap-4">
              <p className="text-xs text-text-3">
                A new editable role will be created with these capabilities selected.
              </p>
              <button
                type="button"
                onClick={() => onCreateRole(detailRole)}
                className="inline-flex shrink-0 items-center justify-center rounded-ctrl border-0 box-border bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover cursor-pointer"
              >
                Create and customize role
              </button>
            </div>
          ) : undefined
        }
      >
        <TemplateSummary role={detailRole} capByCode={capByCode} />

        {/* The body proper starts here, and says so. Without a heading the
            first capability section followed the overview as though it were
            another line of summary — one more area name in a column of grey
            lines, rather than the start of the list the drawer exists for. */}
        {(detailRole.capabilities || []).length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-text m-0" data-testid="capabilities-heading">
              Included capabilities
            </h3>
            <p className="mt-1 text-xs text-text-3">Grouped by functional area.</p>
          </div>
        )}
        <RoleCapabilityGroups capabilities={detailRole.capabilities} capByCode={capByCode} />
      </RightSheet>
    );
  }

  return (
    <RightSheet
      open={open}
      onClose={onClose}
      size="wide"
      title="Start from a template"
      subtitle="Templates are read-only starting points"
      bodyKey="list"
      loading={isLoading}
    >
      <p className="text-xs text-text-2 mb-5">{INTRO}</p>

      {showAll ? (
        <div className="space-y-6">
          {recommended.length > 0 && (
            <div>
              <SectionHeading>Recommended</SectionHeading>
              <TemplateGrid
                templates={recommended}
                capabilityCatalog={capabilityCatalog}
                canManage={canManage}
                onViewDetail={setDetailRole}
              />
            </div>
          )}
          {other.length > 0 && (
            <div>
              <SectionHeading>Other templates</SectionHeading>
              <TemplateGrid
                templates={other}
                capabilityCatalog={capabilityCatalog}
                canManage={canManage}
                onViewDetail={setDetailRole}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          <TemplateGrid
            templates={recommended}
            capabilityCatalog={capabilityCatalog}
            canManage={canManage}
            onViewDetail={setDetailRole}
          />
          {other.length > 0 && (
            <button
              type="button"
              onClick={() => setBrowseAll(true)}
              className="mt-4 text-xs font-medium text-primary hover:text-primary-hover cursor-pointer"
            >
              Browse all {templates.length} templates
            </button>
          )}
        </>
      )}
    </RightSheet>
  );
}

TemplatePickerSheet.propTypes = {
  open: PropTypes.bool,
  scope: PropTypes.oneOf(["recommended", "all"]),
  templates: PropTypes.array,
  capabilityCatalog: PropTypes.array,
  canManage: PropTypes.bool,
  isLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onCreateRole: PropTypes.func,
};
